import styled from "styled-components"

import { colors, Text } from "@minuthq/meatball-ui-react"

import { useGetUser } from "src/data/user/hooks/useGetUser"
import { TClockType } from "src/data/user/user"
import { useTranslate } from "src/i18n/useTranslate"
import { Divider } from "src/ui/Divider/Divider"
import { Box } from "src/ui/Graphs/GenericTooltip"
import { TSchedulerEvent } from "src/ui/Scheduler/schedulerTypes"
import { spacing } from "src/ui/spacing"

export function SchedulerTooltip({ events }: { events: TSchedulerEvent[] }) {
  const { t, langKeys } = useTranslate()
  const { clock_type } = useGetUser()

  if (events.length <= 0) {
    return null
  }

  const hasMultipleEvents = events.length > 1
  const primaryEvent = events[0]

  if (!primaryEvent) {
    return null
  }

  const headerText = hasMultipleEvents
    ? t(langKeys.task_management_count_tasks_tooltip_title, {
        count: events.length,
      })
    : primaryEvent.title

  return (
    <Box>
      <Header>
        <StyledTitleText variant="subtitleS">{headerText}</StyledTitleText>
      </Header>

      <Content>
        {hasMultipleEvents ? (
          <MultipleEventsContent events={events} clockType={clock_type} />
        ) : (
          <SingleEventContent event={primaryEvent} clockType={clock_type} />
        )}
      </Content>
    </Box>
  )
}

function SingleEventContent({
  event,
  clockType,
}: {
  event: TSchedulerEvent
  clockType: TClockType
}) {
  const { t, langKeys } = useTranslate()

  return (
    <>
      <div>
        <Text variant="bodyS" color="tertiary">
          {t(langKeys.start)}: {event.start.format({ clockType })}
        </Text>
      </div>

      <div>
        <Text variant="bodyS" color="tertiary">
          {t(langKeys.end)}: {event.end.format({ clockType })}
        </Text>
      </div>
    </>
  )
}

function MultipleEventsContent({
  events,
  clockType,
}: {
  events: TSchedulerEvent[]
  clockType: TClockType
}) {
  return (
    <div>
      {events.map((event) => (
        <div key={event.id}>
          <MoreEventsGrid>
            <StyledDot
              $backgroundColor={event.fillColor ?? colors.systemError}
              $borderColor={event.borderColor ?? "none"}
            />
            <div>
              <StyledTitleText variant="subtitleS">
                {event.title}
              </StyledTitleText>
              <Text variant="bodyS" color="tertiary" as="span">
                {event.start.format({
                  clockType,
                })}
              </Text>{" "}
              -{" "}
              <Text variant="bodyS" color="tertiary" as="span">
                {event.end.format({
                  clockType,
                })}
              </Text>
            </div>
          </MoreEventsGrid>
          <Divider />
        </div>
      ))}
    </div>
  )
}

const Header = styled.div`
  border-radius: 0.25rem 0.25rem 0 0;
  padding: ${spacing.S} ${spacing.M};
  background: ${colors.primaryLight};
`

const Content = styled.div`
  display: grid;
  gap: ${spacing.XS};
  padding: ${spacing.S} ${spacing.M};
`

const MoreEventsGrid = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: ${spacing.M};
  align-items: center;
`

const StyledDot = styled.div<{
  $backgroundColor: string
  $borderColor: string
}>`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: ${({ $backgroundColor }) => $backgroundColor};
  ${({ $borderColor }) => $borderColor && `border: 1px solid ${$borderColor}`};
`
const StyledTitleText = styled(Text)`
  max-width: 20ch;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`
